import React from 'react';
import BaseModule, { ModuleMetadata, NavigationItem, RouteItem, DashboardWidget, AnalyticsData, HelpContent } from './BaseModule';
import { VideoCall } from '@mui/icons-material';
// Correct imports based on file locations
import PresentationList from './presentations/PresentationList';
import PresentationForm from './presentations/PresentationForm';
import PresentationDetails from './presentations/PresentationDetails';
import ReportViewer from './reporting/ReportViewer';
import RubricDetailView from './reporting/RubricDetailView';

export class PresentationModule extends BaseModule {
    constructor() {
        super();
        this.id = 'presentations';
        this.name = 'Presentation Management';
        this.description = 'Manage presentation uploads, evaluations, and pipeline processing';
        this.version = '1.0.0';
        this.enabled = true;
    }

    /**
     * Get navigation items for the sidebar
     */
    getNavigationItems(user: any, userProfile: any): NavigationItem[] {
        const items: NavigationItem[] = [];

        // All authenticated users can access presentations
        items.push({
            name: 'Presentations',
            icon: <VideoCall />,
            path: '/presentations',
            module: this.id
        });

        return items;
    }

    /**
     * Get React routes for the module
     */
    getRoutes(user: any, userProfile: any): RouteItem[] {
        return [
            {
                path: '/presentations',
                element: <PresentationList />,
                module: this.id
            },
            {
                path: '/presentations/:id',
                element: <PresentationDetails />,
                module: this.id
            },
            {
                path: '/presentations/:id/edit', // Fixed path to match
                element: <PresentationForm />,
                module: this.id
            },
            {
                path: '/presentations/:presentationId/report',
                element: <ReportViewer />,
                module: this.id
            },
            {
                path: '/presentations/:presentationId/rubric',
                element: <RubricDetailView />,
                module: this.id
            }
        ];
    }

    /**
     * Get dashboard widgets for the module
     */
    getDashboardWidgets(user: any, userProfile: any): DashboardWidget[] {
        return [
            {
                id: 'presentation-progress',
                title: 'Evaluation Progress',
                component: 'PresentationProgressWidget',
                module: this.id,
                priority: 1
            },
            {
                id: 'recent-presentations',
                title: 'Recent Presentations',
                component: 'RecentPresentationsWidget',
                module: this.id,
                priority: 2
            }
        ];
    }

    /**
     * Get analytics data for the module
     */
    async getAnalyticsData(user: any, userProfile: any): Promise<AnalyticsData> {
        return {
            totalPresentations: 0,
            completedEvaluations: 0,
            inProgressEvaluations: 0,
            failedEvaluations: 0,
            averageScore: 0,
            averageProcessingTime: 0,
            statusDistribution: {
                NEW: 0,
                IN_PROCESS: 0,
                VIDEO_ANALYSIS: 0,
                ANALYSE_ROLES: 0,
                EVALUATING_STRUCTURE: 0,
                EVALUATING_SOFT_SKILLS: 0,
                EVALUATING_KNOWLEDGE: 0,
                GENERATING_REPORT: 0,
                COMPLETE: 0,
                FAILED: 0
            }
        };
    }

    /**
     * Get help content for the module
     */
    getHelpContent(user: any, userProfile: any): HelpContent {
        return {
            '/presentations': {
                title: 'Presentation Management',
                description: 'Upload and manage presentation videos for AI-powered evaluation using our streamlined wizard. The wizard guides you through a simple 3-step process: Upload Video, Enter Details, and Evaluate. Real-time progress tracking keeps you informed throughout.',
                quickTips: [
                    'Use the "New Presentation" button to launch the evaluation wizard',
                    'Upload video files in common formats (MP4, MOV, AVI, WMV)',
                    'Provide clear metadata including product name and presentation type',
                    'Select an evaluation framework to customize scoring criteria',
                    'Monitor real-time progress through the wizard\'s visual tracker',
                    'View scores immediately when evaluation completes (~2-3 minutes)',
                    'Click "View Full Report" to access detailed feedback and recommendations'
                ],
                faqs: [
                    {
                        question: 'What video formats are supported?',
                        answer: 'The system supports common video formats including MP4, MOV, AVI, and WMV. For best results, use MP4 format with clear audio quality.'
                    },
                    {
                        question: 'How long does evaluation take?',
                        answer: 'The wizard uses our fast V2 evaluation pipeline. Typical presentations complete in 2-3 minutes. You\'ll see real-time progress through three stages: Upload, Video Analysis, and Evaluation.'
                    },
                    {
                        question: 'What happens during the evaluation wizard?',
                        answer: 'Step 1: Upload your video file. Step 2: Enter presentation details (description, product, type, and evaluation framework). Step 3: The system automatically transcribes your video using AI, then performs a comprehensive evaluation covering all categories defined in your evaluation framework using advanced AI analysis.'
                    },
                    {
                        question: 'Can I restart a failed evaluation?',
                        answer: 'Yes, if an evaluation fails, the wizard shows a "Try Again" button. The system automatically retries temporary failures. Administrators can also restart evaluations from the presentation list.'
                    },
                    {
                        question: 'What scores will I see?',
                        answer: 'Upon completion, you\'ll see an overall score plus individual category scores based on your evaluation framework. Click "View Full Report" for detailed feedback, strengths, areas for improvement, and actionable recommendations.'
                    }
                ]
            }
        };
    }

    /**
     * Get Firestore collections used by the module
     */
    getCollections(): string[] {
        return [
            'presentations',
            'evaluationResults',
            'transcriptions',
            'pipelineStatus'
        ];
    }

    /**
     * Get Firestore security rules for the module
     */
    getSecurityRules(): string {
        return `
      // Presentation security rules
      match /presentations/{presentationId} {
        allow read: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId ||
          resource.data.createdBy == request.auth.uid ||
          request.auth.token.role == 'globalAdmin'
        );
        allow create: if request.auth != null && (
          request.resource.data.tenantId == request.auth.token.tenantId &&
          request.resource.data.createdBy == request.auth.uid
        );
        allow update: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId &&
          (resource.data.createdBy == request.auth.uid || 
           request.auth.token.role == 'tenantAdmin' || 
           request.auth.token.role == 'globalAdmin')
        );
        allow delete: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId &&
          (resource.data.createdBy == request.auth.uid || 
           request.auth.token.role == 'tenantAdmin' || 
           request.auth.token.role == 'globalAdmin')
        );
      }

      // Evaluation results (read-only for presenters)
      match /evaluationResults/{resultId} {
        allow read: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId ||
          request.auth.token.role == 'globalAdmin'
        );
        allow write: if request.auth != null && (
          request.auth.token.role == 'tenantAdmin' || 
          request.auth.token.role == 'globalAdmin'
        );
      }

      // Transcriptions (system generated)
      match /transcriptions/{transcriptionId} {
        allow read: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId ||
          request.auth.token.role == 'globalAdmin'
        );
      }

      // Pipeline status (system managed)
      match /pipelineStatus/{statusId} {
        allow read: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId ||
          request.auth.token.role == 'globalAdmin'
        );
      }
    `;
    }

    /**
     * Check if the module is available for the current user
     */
    isAvailable(user: any, userProfile: any): boolean {
        return this.enabled && !!user && !!userProfile;
    }
}

export default PresentationModule;
